import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionList from 'assets/components/data/themes/Theme.Collection.List';
import DataMap from 'assets/components/dataMap/DataMap';
import SortButton from 'assets/components/filters/sortButton/SortButton';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, defaultDateTimeFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { cleanValues } from 'assets/utils/data/Object';
import { combineStrings } from 'assets/utils/data/String';
import dayjs from 'dayjs';
import { merge } from 'lodash';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import Contract from 'models/sales/contract/Model.Contract';
import useClaimCreator from 'module/claims/hooks/useClaimCreator';
import React, { useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { allowedContractStatusesForClaimCreation } from '../../claim/Claim.Hooks';
import ClaimSimpleClaimForm from '../../claim/simpleClaim/Claim.SimpleClaimForm';
import { useContractProvider } from '../Contract.Hooks';
import ContractFilters from './Contract.Filters';
import ContractInfo from './Contract.Info';

export default function ContractList() {
  const { lang } = getLocales();
  const { listApi, pageRouter } = useContractProvider();
  const { canCreateClaim, canCreateSimpleClaim, canCreateClaimForExpiredContract, createClaim } = useClaimCreator();
  const [simpleClaimData, setSimpleClaimData] = useState(undefined);
  const [selected, select] = useState<{ contract: Contract; isForExpired: boolean }>({
    contract: null,
    isForExpired: false,
  });
  function handleSort(newFilters: Api.IDefaultListRequest) {
    pageRouter.updateQuery({
      ...pageRouter.query,
      list: cleanValues({
        ...pageRouter.query?.list,
        ...newFilters,
      }),
    });
  }
  return (
    <>
      {selected.contract && (
        <Confirm
          data={selected}
          onConfirm={(data) => createClaim(data.contract, data.isForExpired)}
          onClose={() => select({ contract: null, isForExpired: false })}
          actionName={lang.createClaimForContract}
          itemName={selected.contract.displayInfo.title}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}
      <Page
        class={ThemePageList}
        isLoading={listApi.isExecuting}
        sidebar={<ContractInfo />}
        header={
          <>
            <b style={{ marginRight: 'auto' }} children={lang.contract} />
            {isMobile && <ContractFilters />}
          </>
        }
        render={
          <Collection class={ThemeCollectionList}>
            {isDesktop && <ContractFilters />}
            <table className={ThemeTableSimple}>
              <thead>
                <tr>
                  <th>
                    {lang.appNumber}
                    <SortButton
                      orderBy="appNumber"
                      thenBy={['client.FirstName', 'client.LastName', 'vehicle.Vin']}
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.product}
                    <SortButton orderBy="product.name" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>
                    {lang.vehicle}/{lang.vin}
                    <SortButton
                      orderBy="vehicle.makeCode"
                      thenBy="vehicle.model"
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.client}
                    <SortButton
                      orderBy="client.firstName"
                      thenBy="client.lastName"
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.dealer}
                    <SortButton orderBy="dealer.name" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>
                    {lang.createdBy}
                    <SortButton
                      orderBy="createdBy.firstName"
                      thenBy="createdBy.lastName"
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.createdAt}
                    <SortButton orderBy="createdAtUtc" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>
                    {lang.expiryDate}
                    <SortButton orderBy="expiryDate" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>{lang.noOfClaims}</th>
                  <th>{lang.actions}</th>
                </tr>
              </thead>
              <tbody>
                <DataMap
                  data={listApi.payload?.data}
                  render={({ data: contract }) => {
                    return (
                      <React.Fragment key={contract.id}>
                        <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                          <td>
                            <b>{contract.appNumberWithRevision}</b>
                            <br />
                            {contract.statusCode?.replace('_', ' - ')}
                          </td>
                          <td>{contract.product?.name || lang.unknownProduct}</td>
                          <td>
                            <span>
                              {contract.vehicle.makeCode} {contract.vehicle.model}
                            </span>
                            <span> {contract.vehicle.year?.toString()}</span>
                            <br />
                            <span>{contract.vehicle?.vin}</span>
                          </td>
                          <td>{combineStrings(' ', contract.client?.firstName, contract.client?.lastName)}</td>
                          <td>{contract.dealer?.name}</td>
                          <td>{combineStrings(' ', contract.createdBy?.fullName) || lang.unknownUser}</td>
                          <td>
                            {dayjs(contract?.createdAtLocal, defaultServerDateTimeFormat).format(defaultDateTimeFormat)}
                          </td>
                          <td>{dayjs(contract?.expiryDate, defaultServerDateTimeFormat).format(defaultDateFormat)}</td>
                          <td>{contract.claimCount ?? 'N / A'} </td>
                          <td style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                            <Button
                              class={ThemeButtonCircle}
                              media={<Icon class="fas-info-circle" />}
                              onClick={pageRouter.updateParams}
                              data={{ id: contract.id.toString() }}
                              htmlElementProps={{
                                title: lang.info,
                              }}
                            />
                            <Button
                              class={ThemeButtonCircle}
                              media={<Icon class="fas-car-crash" />}
                              data={{ contract, isForExpired: contract.statusCode === ContractStatusCode.PAID_EXPIRED }}
                              onClick={select}
                              disabled={
                                !(
                                  allowedContractStatusesForClaimCreation.includes(
                                    contract.statusCode as ContractStatusCode
                                  ) && canCreateClaim
                                ) &&
                                !(
                                  contract.statusCode === ContractStatusCode.PAID_EXPIRED &&
                                  canCreateClaimForExpiredContract
                                )
                              }
                              htmlElementProps={{
                                title:
                                  contract.statusCode === ContractStatusCode.PAID_EXPIRED
                                    ? lang.createClaimForExpiredContract
                                    : lang.createClaimForContract,
                              }}
                            />
                            <Button
                              class={ThemeButtonCircle}
                              media={<Icon class="fas-car-side" />}
                              data={contract}
                              onClick={setSimpleClaimData}
                              disabled={
                                !(
                                  allowedContractStatusesForClaimCreation.includes(
                                    contract.statusCode as ContractStatusCode
                                  ) && canCreateSimpleClaim
                                )
                              }
                              htmlElementProps={{
                                title: lang.createSimpleClaim,
                              }}
                            />
                            <LinkButton
                              class={ThemeButtonCircle}
                              url={contract.documentationAggregate?.url}
                              media={<Icon class="fas-download" />}
                              target="_blank"
                              disabled={!contract?.documentationAggregate?.url}
                              htmlElementProps={{
                                title: !contract?.documentationAggregate?.url
                                  ? lang.missingContractDocument
                                  : lang.download,
                              }}
                            />
                          </td>
                        </tr>
                        {simpleClaimData && (
                          <ClaimSimpleClaimForm
                            contract={simpleClaimData}
                            onClose={() => setSimpleClaimData(undefined)}
                          />
                        )}
                      </React.Fragment>
                    );
                  }}
                />
              </tbody>
            </table>
          </Collection>
        }
        footer={
          <Pagination
            class={ThemePaginationRounded}
            count={listApi.payload?.count}
            offset={listApi.body?.offset}
            limit={listApi.body?.limit}
            onLimitChange={(limit) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    limit,
                    offset: 0,
                  },
                })
              )
            }
            onOffsetChange={(offset) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    offset,
                  },
                })
              )
            }
          />
        }
      />
    </>
  );
}
